import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import MarkdownEditor from '@/components/advisor/common/MarkdownEditor';
import {
  FileText,
  Plus,
  Loader2,
  Eye,
  Pencil,
  Trash2,
  Clock,
  CheckCircle,
  Save,
  ArrowLeft,
  Tag,
  Mail,
} from 'lucide-react';
interface BlogPost {
  id: string;
  title: string;
  slug: string;
  ai_assisted: boolean;
  excerpt: string;
  content: string;
  tags: string;
  status: string;
  status_display: string;
  published_at: string | null;
  email_subscribers: boolean;
  emailed_at: string | null;
  view_count: number;
  created_at: string;
  updated_at: string;
}

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-slate-500/10 text-slate-700 border-slate-500/30',
  published: 'bg-green-500/10 text-green-700 border-green-500/30',
};

function slugify(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

export default function BlogManagementPage() {
  const queryClient = useQueryClient();
  const [view, setView] = useState<'list' | 'editor'>('list');
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [form, setForm] = useState({ title: '', slug: '', excerpt: '', content: '', tags: '', status: 'draft', email_subscribers: false, ai_assisted: false });
  const [showPreview, setShowPreview] = useState(false);

  const { data: posts, isLoading } = useQuery<BlogPost[]>({
    queryKey: ['blog-posts'],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.get('/advisor/blog/');
      return res.data;
    },
  });

  // Stats calculated locally from posts list (no separate endpoint needed)

  const createMutation = useMutation({
    mutationFn: async (overrideStatus?: string) => {
      const api = await authenticatedBitswanBackendInstance();
      return api.post('/advisor/blog/', {
        title: form.title,
        slug: form.slug || slugify(form.title),
        excerpt: form.excerpt,
        content: form.content,
        tags: form.tags,
        status: overrideStatus || form.status,
        email_subscribers: form.email_subscribers,
        ai_assisted: form.ai_assisted,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blog-posts'] });
      queryClient.invalidateQueries({ queryKey: ['blog-stats'] });
      setView('list');
      setForm({ title: '', slug: '', excerpt: '', content: '', tags: '', status: 'draft', email_subscribers: false, ai_assisted: false });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (overrideStatus?: string) => {
      if (!editingPost) return;
      const api = await authenticatedBitswanBackendInstance();
      return api.patch(`/advisor/blog/${editingPost.id}/`, {
        title: form.title,
        slug: form.slug || slugify(form.title),
        excerpt: form.excerpt,
        content: form.content,
        tags: form.tags,
        status: overrideStatus || form.status,
        email_subscribers: form.email_subscribers,
        ai_assisted: form.ai_assisted,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blog-posts'] });
      queryClient.invalidateQueries({ queryKey: ['blog-stats'] });
      setView('list');
      setEditingPost(null);
      setForm({ title: '', slug: '', excerpt: '', content: '', tags: '', status: 'draft', email_subscribers: false, ai_assisted: false });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (postId: string) => {
      const api = await authenticatedBitswanBackendInstance();
      return api.delete(`/advisor/blog/${postId}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blog-posts'] });
      queryClient.invalidateQueries({ queryKey: ['blog-stats'] });
    },
  });

  const openEditor = (post?: BlogPost) => {
    if (post) {
      setEditingPost(post);
      setForm({
        title: post.title,
        slug: post.slug,
        ai_assisted: Boolean(post.ai_assisted),
        excerpt: post.excerpt,
        content: post.content,
        tags: post.tags,
        status: post.status,
        email_subscribers: post.email_subscribers,
      });
    } else {
      setEditingPost(null);
      setForm({ title: '', slug: '', excerpt: '', content: '', tags: '', status: 'draft', email_subscribers: false, ai_assisted: false });
    }
    setView('editor');
    setShowPreview(false);
  };

  if (isLoading) {
    return <div className="flex items-center justify-center p-8"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>;
  }

  // ═══ EDITOR VIEW ═══
  if (view === 'editor') {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => setView('list')}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <div>
              <h2 className="text-2xl font-bold tracking-tight">
                {editingPost ? 'Edit Post' : 'New Post'}
              </h2>
              <p className="text-muted-foreground">Write and publish blog content for lead generation</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPreview(!showPreview)}
              data-testid="preview-btn"
            >
              <Eye className="h-4 w-4 mr-1" /> {showPreview ? 'Edit' : 'Preview'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => editingPost ? updateMutation.mutate('draft') : createMutation.mutate('draft')}
              disabled={!form.title.trim() || createMutation.isPending || updateMutation.isPending}
              data-testid="save-draft-btn"
            >
              <Save className="h-4 w-4 mr-1" /> Save Draft
            </Button>
            <Button
              size="sm"
              onClick={() => editingPost ? updateMutation.mutate('published') : createMutation.mutate('published')}
              disabled={!form.title.trim() || !form.content.trim() || createMutation.isPending || updateMutation.isPending}
              data-testid="publish-btn"
            >
              {(createMutation.isPending || updateMutation.isPending) ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-1" />
              )}
              Publish
            </Button>
          </div>
        </div>

        {showPreview ? (
          // ═══ PREVIEW MODE ═══
          <Card data-testid="post-preview">
            <CardContent className="py-8 max-w-2xl mx-auto">
              <h1 className="text-3xl font-bold mb-2">{form.title || 'Untitled'}</h1>
              {form.tags && (
                <div className="flex gap-1.5 mb-4">
                  {form.tags.split(',').map((t, i) => (
                    <Badge key={i} variant="outline" className="text-xs">{t.trim()}</Badge>
                  ))}
                </div>
              )}
              {form.excerpt && (
                <p className="text-lg text-muted-foreground mb-6 italic">{form.excerpt}</p>
              )}
              <div className="prose prose-sm dark:prose-invert max-w-none whitespace-pre-wrap">
                {form.content || 'No content yet...'}
              </div>
            </CardContent>
          </Card>
        ) : (
          // ═══ EDIT MODE ═══
          <div className="space-y-4" data-testid="blog-editor">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Title</label>
              <Input
                value={form.title}
                onChange={(e) => {
                  const title = e.target.value;
                  setForm(prev => ({
                    ...prev,
                    title,
                    slug: prev.slug || slugify(title),
                  }));
                }}
                placeholder="5 Signs Your Manufacturing Plant Is Ready for Process Automation"
                className="text-lg font-medium"
                data-testid="post-title"
              />
            </div>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground">Tags (comma-separated)</label>
                <Input
                  value={form.tags}
                  onChange={(e) => setForm(prev => ({ ...prev, tags: e.target.value }))}
                  placeholder="manufacturing, SAP, automation"
                  className="text-sm"
                  data-testid="post-tags"
                />
              </div>
            </div>
            <label
              className="flex cursor-pointer items-start gap-2 rounded-md border p-3"
              data-testid="post-email-subscribers-row"
            >
              <input
                type="checkbox"
                className="mt-0.5"
                checked={form.email_subscribers}
                disabled={Boolean(editingPost?.emailed_at)}
                onChange={(e) => setForm(prev => ({ ...prev, email_subscribers: e.target.checked }))}
                data-testid="post-email-subscribers"
              />
              <span className="text-sm">
                <span className="font-medium">Send this to my mailing list when I publish it</span>
                <span className="block text-xs text-muted-foreground">
                  {editingPost?.emailed_at
                    ? `Already sent on ${new Date(editingPost.emailed_at).toLocaleString()}. Publishing again will not send it twice.`
                    : 'Subscribers get the post by email. It goes once, when the post is first published.'}
                </span>
              </span>
            </label>

            <div>
              <label className="text-xs font-medium text-muted-foreground">Excerpt (for previews)</label>
              <Input
                value={form.excerpt}
                onChange={(e) => setForm(prev => ({ ...prev, excerpt: e.target.value }))}
                placeholder="A brief summary that appears in search results and social shares..."
                className="text-sm"
                data-testid="post-excerpt"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Content</label>
              <div className="mt-1" data-testid="post-content">
                <MarkdownEditor
                  value={form.content}
                  onChange={(val) => setForm(prev => ({ ...prev, content: val }))}
                  height="400px"
                />
              </div>
            </div>
            <label className="flex items-start gap-3 rounded-lg border p-3" data-testid="post-ai-assisted">
              <Switch
                checked={form.ai_assisted}
                onCheckedChange={(checked) => setForm(prev => ({ ...prev, ai_assisted: checked }))}
                aria-label="Written with AI assistance"
              />
              <span className="text-sm">
                <span className="font-medium">{form.ai_assisted ? 'Written with AI assistance' : 'Purely human-written'}</span>
                <span className="block text-xs text-muted-foreground">
                  Readers see this on the post.
                </span>
              </span>
            </label>
            <div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ═══ LIST VIEW ═══
  const published = (posts || []).filter(p => p.status === 'published');
  const drafts = (posts || []).filter(p => p.status === 'draft');
  const totalViews = (posts || []).reduce((s, p) => s + p.view_count, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Blog</h2>
          <p className="text-muted-foreground">Write articles to generate inbound leads</p>
        </div>
        <Button onClick={() => openEditor()} data-testid="new-post-btn">
          <Plus className="h-4 w-4 mr-2" /> New Post
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3" data-testid="blog-stats">
        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <div className="text-2xl font-bold text-amber-600">{(posts || []).length}</div>
            <div className="text-xs text-muted-foreground">Total Posts</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <div className="text-2xl font-bold text-green-600">{published.length}</div>
            <div className="text-xs text-muted-foreground">Published</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <div className="text-2xl font-bold text-slate-600">{drafts.length}</div>
            <div className="text-xs text-muted-foreground">Drafts</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <div className="text-2xl font-bold text-blue-600">{totalViews}</div>
            <div className="text-xs text-muted-foreground">Total Views</div>
          </CardContent>
        </Card>
      </div>

      {/* Post List */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <FileText className="h-4 w-4 text-amber-500" /> Posts
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(posts || []).length > 0 ? (
            <div className="space-y-2" data-testid="posts-list">
              {(posts || []).map(post => (
                <div
                  key={post.id}
                  className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/30 cursor-pointer transition-colors"
                  onClick={() => openEditor(post)}
                  data-testid={`post-row-${post.id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="h-4 w-4 text-amber-500 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{post.title}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge
                          variant="outline"
                          className={`text-[10px] ${STATUS_COLORS[post.status] || ''}`}
                        >
                          {post.status === 'published' ? <CheckCircle className="h-2.5 w-2.5 mr-0.5" /> : <Clock className="h-2.5 w-2.5 mr-0.5" />}
                          {post.status_display || post.status}
                        </Badge>
                        <Badge variant="outline" className="text-[10px]" data-testid={`post-authorship-${post.id}`}>
                          {post.ai_assisted ? 'AI-assisted' : 'Human-written'}
                        </Badge>
                        {post.emailed_at && (
                          <Badge variant="outline" className="text-[10px]" data-testid={`post-emailed-${post.id}`}>
                            <Mail className="h-2.5 w-2.5 mr-0.5" />
                            Emailed to subscribers
                          </Badge>
                        )}
                        {post.tags && (
                          <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                            <Tag className="h-2.5 w-2.5" /> {post.tags}
                          </span>
                        )}
                        <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                          <Eye className="h-2.5 w-2.5" /> {post.view_count} views
                        </span>
                        <span className="text-[10px] text-muted-foreground">
                          {new Date(post.updated_at || post.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={(e) => { e.stopPropagation(); openEditor(post); }}
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                      onClick={(e) => { e.stopPropagation(); deleteMutation.mutate(post.id); }}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12" data-testid="no-posts">
              <FileText className="h-12 w-12 text-muted-foreground/30 mb-4" />
              <p className="text-muted-foreground font-medium">No blog posts yet</p>
              <p className="text-sm text-muted-foreground mt-1 text-center max-w-md">
                Write articles to establish expertise and generate inbound leads.
                Click "New Post" to get started.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
